import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { GraduationCap, RotateCcw, Search } from "lucide-react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { BottomSheet } from "@/components/app/bottom-sheet";
import { MASTER_DATABASE, type CatKey } from "@/lib/college/data";
import { PaidOnly } from "@/components/billing/feature-lock";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/college-predictor")({
  head: () => ({
    ...seo({
      title: "College Predictor — JEE & NEET Rank vs Cutoff",
      description:
        "Enter your JEE or NEET rank and category to see eligible IITs, NITs, IIITs and government medical colleges with branch-wise opening and closing cutoffs.",
      path: "/college-predictor",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "College Predictor", path: "/college-predictor" },
      ]),
    ],
  }),
  component: CollegePredictorRoute,
});

const CARD =
  "rounded-3xl border border-white/10 bg-card/70 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl";

const FIELD =
  "w-full rounded-2xl border border-white/10 bg-background/60 px-3.5 py-2.5 text-sm font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/60";

const CATEGORIES: { id: CatKey; label: string }[] = [
  { id: "GEN", label: "General / OPEN (CRL)" },
  { id: "OBC", label: "OBC-NCL" },
  { id: "SC", label: "SC Category" },
  { id: "ST", label: "ST Category" },
  { id: "EWS", label: "EWS Category" },
];

/** College Predictor is subscription-only: no free preview of any kind. */
function CollegePredictorRoute() {
  return (
    <PaidOnly
      title="College Predictor"
      description="Rank-vs-cutoff predictions for IITs, NITs, IIITs, GFTIs and government medical colleges are available with any active plan."
      reason="College Predictor"
    >
      <CollegePredictorPage />
    </PaidOnly>
  );
}

function CollegePredictorPage() {
  const [stream, setStream] = useState<"ENGG" | "MED">("ENGG");
  const [category, setCategory] = useState<CatKey>("GEN");
  const [userRank, setUserRank] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [branchFilter, setBranchFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalCollege, setModalCollege] = useState<string | null>(null);

  const availableTypes = useMemo(() => {
    const items = MASTER_DATABASE.filter((c) => c.exam === stream);
    return ["ALL", ...new Set(items.map((i) => i.type))];
  }, [stream]);

  const availableBranches = useMemo(() => {
    const items = MASTER_DATABASE.filter((c) => c.exam === stream);
    const bSet = new Set<string>();
    items.forEach((c) => c.branches.forEach((b) => bSet.add(b.name)));
    return ["ALL", ...Array.from(bSet)];
  }, [stream]);

  const filteredData = useMemo(() => {
    const rows: {
      collegeId: string;
      collegeName: string;
      city: string;
      state: string;
      type: string;
      nirf: number;
      branchName: string;
      catRanks: { op: number; cl: number };
    }[] = [];
    const query = searchQuery.toLowerCase().trim();

    MASTER_DATABASE.filter((college) => college.exam === stream).forEach((college) => {
      if (typeFilter !== "ALL" && college.type !== typeFilter) return;
      if (query) {
        const matchName = college.name.toLowerCase().includes(query);
        const matchCity = college.city.toLowerCase().includes(query);
        const matchState = college.state.toLowerCase().includes(query);
        if (!matchName && !matchCity && !matchState) return;
      }
      college.branches.forEach((branch) => {
        if (branchFilter !== "ALL" && branch.name !== branchFilter) return;
        rows.push({
          collegeId: college.id,
          collegeName: college.name,
          city: college.city,
          state: college.state,
          type: college.type,
          nirf: college.nirf,
          branchName: branch.name,
          catRanks: branch[category] || { op: 0, cl: 0 },
        });
      });
    });

    return rows.sort((a, b) => a.catRanks.cl - b.catRanks.cl);
  }, [stream, category, typeFilter, branchFilter, searchQuery]);

  const handleStreamSwitch = (newStream: "ENGG" | "MED") => {
    setStream(newStream);
    setTypeFilter("ALL");
    setBranchFilter("ALL");
  };

  const resetFilters = () => {
    setUserRank("");
    setCategory("GEN");
    setTypeFilter("ALL");
    setBranchFilter("ALL");
    setSearchQuery("");
  };

  const parsedRank = userRank ? parseInt(userRank, 10) : null;
  const detail = modalCollege ? MASTER_DATABASE.find((c) => c.id === modalCollege) : null;

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pt-6">
      <header className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-2xl border border-white/10 bg-card/60 backdrop-blur-xl">
          <GraduationCap className="size-5 text-indigo-500 dark:text-indigo-300" />
        </span>
        <div>
          <h1 className="text-lg font-bold tracking-tight">College Predictor</h1>
          <p className="text-[11px] text-muted-foreground">
            IITs, NITs, IIITs & Government Medical Colleges cutoff database
          </p>
        </div>
        <ShareButton className="ml-auto" />
      </header>


      {/* Stream selector */}
      <div className="mt-5 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-card/50 p-1 backdrop-blur-xl">
        {(
          [
            { id: "ENGG", label: "Engineering (JEE)" },
            { id: "MED", label: "Medical (NEET)" },
          ] as const
        ).map((s) => (
          <button
            key={s.id}
            onClick={() => handleStreamSwitch(s.id)}
            className={cn(
              "rounded-xl px-3 py-2 text-[13px] font-semibold transition-all active:scale-95",
              stream === s.id
                ? "bg-primary text-primary-foreground shadow"
                : "text-muted-foreground",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className={cn(CARD, "mt-4 p-4")}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold tracking-tight">
              {stream === "ENGG"
                ? "Engineering College & Branch Predictor"
                : "Medical College (MBBS/NEET) Predictor"}
            </h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Enter your rank and category to see eligible colleges and their cutoffs.
            </p>
          </div>
          <button
            onClick={resetFilters}
            className="flex shrink-0 items-center gap-1 rounded-full border border-white/10 px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground transition-all active:scale-95"
          >
            <RotateCcw className="size-3" />
            Clear
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Your AIR / Rank
            </span>
            <input
              type="number"
              inputMode="numeric"
              value={userRank}
              onChange={(e) => setUserRank(e.target.value)}
              placeholder="e.g. 1500"
              className={FIELD}
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Category
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as CatKey)}
              className={FIELD}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Institute Type
            </span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={FIELD}
            >
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {t === "ALL" ? "All Types" : t}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Branch / Program
            </span>
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className={FIELD}
            >
              {availableBranches.map((b) => (
                <option key={b} value={b}>
                  {b === "ALL" ? "All Branches" : b}
                </option>
              ))}
            </select>
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Search College / City
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g. Bombay, Delhi"
                className={cn(FIELD, "pl-9")}
              />
            </div>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
          <span className="text-[11px] font-semibold text-muted-foreground">Quick category:</span>
          {(["GEN", "OBC", "SC", "ST", "EWS"] as CatKey[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all active:scale-95",
                category === cat
                  ? "border-primary/50 bg-primary/15 text-primary"
                  : "border-white/10 text-muted-foreground",
              )}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary">
            {filteredData.length} results
          </span>
        </div>
      </motion.section>

      {/* Results */}
      <section className="mt-4 space-y-3">
        {filteredData.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 px-6 py-10 text-center">
            <p className="text-sm font-semibold">No college or branch matched</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Adjust your filters, branch selection or rank.
            </p>
            <button
              onClick={resetFilters}
              className="mt-4 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold transition-all active:scale-95"
            >
              Reset filters
            </button>
          </div>
        ) : (
          filteredData.map((item, idx) => {
            const closing = item.catRanks.cl;
            const opening = item.catRanks.op;
            let status: { label: string; className: string } | null = null;
            if (parsedRank) {
              if (parsedRank <= Math.round(closing * 0.9)) {
                status = {
                  label: "High Chance",
                  className: "border-emerald-500/20 bg-emerald-500/10 text-emerald-500",
                };
              } else if (parsedRank <= Math.round(closing * 1.15)) {
                status = {
                  label: "Moderate",
                  className: "border-amber-500/20 bg-amber-500/10 text-amber-500",
                };
              } else {
                status = {
                  label: "Tough",
                  className: "border-rose-500/20 bg-rose-500/10 text-rose-500",
                };
              }
            }
            return (
              <div key={`${item.collegeId}-${item.branchName}-${idx}`} className={cn(CARD, "p-4")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold leading-tight">{item.collegeName}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {item.city}, {item.state} • NIRF #{item.nirf}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] font-semibold text-primary">
                    {item.type}
                  </span>
                </div>

                <p className="mt-3 text-[12px] font-semibold">{item.branchName}</p>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-muted-foreground">
                    Open {opening.toLocaleString()}
                  </span>
                  <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono font-bold text-primary">
                    Close ({category}) {closing.toLocaleString()}
                  </span>
                  {status ? (
                    <span
                      className={cn(
                        "rounded-full border px-2.5 py-1 font-semibold",
                        status.className,
                      )}
                    >
                      {status.label}
                    </span>
                  ) : null}
                  <button
                    onClick={() => setModalCollege(item.collegeId)}
                    className="ml-auto rounded-full border border-white/10 px-3 py-1 font-semibold text-muted-foreground transition-all active:scale-95"
                  >
                    All branches
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>

      <BottomSheet
        open={detail !== null}
        onClose={() => setModalCollege(null)}
        title={detail?.name ?? ""}
        subtitle={
          detail ? `${detail.type} • ${detail.city}, ${detail.state} • NIRF #${detail.nirf}` : ""
        }
        tall
      >
        {detail ? (
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-left text-[11px]">
              <thead className="border-b border-white/10 bg-background/50 uppercase text-muted-foreground">
                <tr>
                  <th className="p-2.5 font-bold">Branch</th>
                  <th className="p-2.5 text-center font-bold">GEN</th>
                  <th className="p-2.5 text-center font-bold">OBC</th>
                  <th className="p-2.5 text-center font-bold">SC</th>
                  <th className="p-2.5 text-center font-bold">ST</th>
                  <th className="p-2.5 text-center font-bold">EWS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 font-mono">
                {detail.branches.map((b) => (
                  <tr key={b.name}>
                    <td className="p-2.5 font-sans font-semibold">{b.name}</td>
                    <td className="whitespace-nowrap p-2.5 text-center">
                      {b.GEN.op}-{b.GEN.cl}
                    </td>
                    <td className="whitespace-nowrap p-2.5 text-center">
                      {b.OBC.op}-{b.OBC.cl}
                    </td>
                    <td className="whitespace-nowrap p-2.5 text-center">
                      {b.SC.op}-{b.SC.cl}
                    </td>
                    <td className="whitespace-nowrap p-2.5 text-center">
                      {b.ST.op}-{b.ST.cl}
                    </td>
                    <td className="whitespace-nowrap p-2.5 text-center">
                      {b.EWS.op}-{b.EWS.cl}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </BottomSheet>
    </div>
  );
}
